import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader2, Mail, MailX, X } from 'lucide-react';
import type { RfqProject, SupplierInvite, SupplierOrg, VendorWorkType } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import {
  buildRfqQuoteRejectionEmailHtml,
  defaultRfqQuoteRejectionEmailMessage,
  rfqQuoteRejectionEmailSubject,
} from '../../utils/rfqQuoteRejectionEmailPreview';
import { rfqProjectEmailDescriptor } from '../../utils/rfqPublishEmailPreview';
import {
  groupQuoteRejectionTargetsForEmail,
  quoteRejectionTargetKey,
  type QuoteRejectionTarget,
} from '../../utils/rfqQuoteRejectionTargets';
import { sendSupplierQuoteRejectionEmails } from '../../utils/sendSupplierQuoteRejectionEmails';
import {
  recipientFirstNameForOrg,
  resolveOrgsMissingRfqNotificationFirstName,
  resolvePublishRecipientsForOrgs,
} from '../../utils/vendorOrgSummary';
import './SupplierQuoteApprovalModal.css';
import './SupplierQuoteRejectionModal.css';

export type SupplierQuoteRejectionModalProps = {
  open: boolean;
  targets: QuoteRejectionTarget[];
  project: RfqProject;
  workType: VendorWorkType;
  projectNumber?: string;
  projectName?: string;
  supplierOrgById: Map<string, SupplierOrg>;
  supplierInvites: SupplierInvite[];
  onClose: () => void;
};

function callableErrorMessage(err: unknown, fallback: string): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
  const message = err instanceof Error ? err.message : fallback;
  if (code === 'functions/not-found' || /not[- ]found/i.test(message)) {
    return 'Rejection emails are not deployed yet. Quotes were not updated.';
  }
  if (code === 'functions/unauthenticated') return 'Sign in again to send notices.';
  if (code === 'functions/permission-denied') return 'You do not have permission to send supplier notices.';
  return message || fallback;
}

function emailHtmlForInPagePreview(html: string): string {
  const body = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  return (body?.[1] || html).trim();
}

const SupplierQuoteRejectionModal: React.FC<SupplierQuoteRejectionModalProps> = ({
  open,
  targets,
  project,
  workType,
  projectNumber,
  projectName,
  supplierOrgById,
  supplierInvites,
  onClose,
}) => {
  const { user } = useAuth();
  const { updateRfqSubmission } = useCRM();
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [previewKey, setPreviewKey] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [messageDirty, setMessageDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<string | null>(null);
  const [sendEmail, setSendEmail] = useState(true);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const dealTitle = project.dealTitle || project.dealId;
  const senderName = (user?.name || user?.email || '').trim();

  const selectedTargets = useMemo(
    () => targets.filter(t => selectedKeys.has(quoteRejectionTargetKey(t))),
    [targets, selectedKeys],
  );

  const previewTarget = useMemo(() => {
    const fallback = selectedTargets[0] ?? targets[0];
    const key = previewKey || (fallback ? quoteRejectionTargetKey(fallback) : '');
    return targets.find(t => quoteRejectionTargetKey(t) === key) ?? fallback ?? null;
  }, [previewKey, selectedTargets, targets]);

  const previewFirstName = previewTarget
    ? recipientFirstNameForOrg(previewTarget.orgId, supplierOrgById) || 'there'
    : 'there';

  useEffect(() => {
    if (!open || !targets.length) return;
    setSelectedKeys(new Set(targets.map(quoteRejectionTargetKey)));
    setPreviewKey(targets[0] ? quoteRejectionTargetKey(targets[0]) : '');
    setRejectionReason('');
    setCustomMessage('');
    setMessageDirty(false);
    setError(null);
    setResultSummary(null);
    setProgressLabel('');
    setSendEmail(true);
  }, [open, targets]);

  const previewQuoteLabel = previewTarget
    ? previewTarget.versionLabel
      ? `${previewTarget.versionLabel} ${previewTarget.quoteLabel}`
      : previewTarget.quoteLabel
    : '';

  useEffect(() => {
    if (!open || messageDirty || !previewTarget) return;
    setCustomMessage(
      defaultRfqQuoteRejectionEmailMessage({
        recipientFirstName: previewFirstName,
        quoteLabel: previewQuoteLabel,
        workType,
        rejectionReason,
      }),
    );
  }, [open, messageDirty, previewTarget, previewFirstName, previewQuoteLabel, workType, rejectionReason]);

  const recipientRows = useMemo(
    () => resolvePublishRecipientsForOrgs(selectedTargets.map(t => t.orgId), supplierOrgById, supplierInvites),
    [selectedTargets, supplierOrgById, supplierInvites],
  );

  const missingFirstName = useMemo(
    () => resolveOrgsMissingRfqNotificationFirstName(selectedTargets.map(t => t.orgId), supplierOrgById),
    [selectedTargets, supplierOrgById],
  );

  const sendableTargets = useMemo(
    () =>
      selectedTargets.filter(t => {
        const row = recipientRows.find(r => r.orgId === t.orgId);
        return !!row?.emails.length && !!row.notificationFirstName;
      }),
    [selectedTargets, recipientRows],
  );

  useEffect(() => {
    if (!open) return;
    if (sendableTargets.length === 0) setSendEmail(false);
  }, [open, sendableTargets.length]);

  const emailSubject = useMemo(
    () =>
      rfqQuoteRejectionEmailSubject(
        projectNumber,
        rfqProjectEmailDescriptor({ descriptor: projectName, dealTitle }),
        workType,
      ),
    [projectNumber, projectName, dealTitle, workType],
  );

  const emailHtml = useMemo(() => {
    if (!previewTarget) return '';
    return buildRfqQuoteRejectionEmailHtml({
      recipientFirstName: previewFirstName,
      vendorOrgName: previewTarget.orgName,
      senderName,
      dealTitle,
      projectNumber,
      projectName,
      workType,
      customMessage,
      quoteLabel: previewQuoteLabel,
      rejectionReason,
    });
  }, [
    previewTarget,
    previewFirstName,
    senderName,
    dealTitle,
    projectNumber,
    projectName,
    workType,
    customMessage,
    previewQuoteLabel,
    rejectionReason,
  ]);

  const toggleTarget = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const persistRejected = async (rows: QuoteRejectionTarget[], emailedOrgIds: Set<string>) => {
    const now = new Date();
    await Promise.all(
      rows.flatMap(row => {
        const submissionIds = [row.submissionId, ...(row.extraSubmissionIds ?? [])].filter(
          (id): id is string => !!id,
        );
        return submissionIds.map(submissionId =>
          updateRfqSubmission(submissionId, {
            rejectedAt: now,
            rejectedByUid: user?.id,
            rejectedByName: user?.name || user?.email || '',
            rejectionReason: rejectionReason.trim(),
            ...(emailedOrgIds.has(row.orgId) ? { rejectionNoticeSentAt: now } : {}),
          }),
        );
      }),
    );
  };

  const willSendEmail = sendEmail && sendableTargets.length > 0;

  const handleSend = async () => {
    if (submitting) return;
    if (!selectedTargets.length) {
      setError('Select at least one quote to reject.');
      return;
    }
    const reason = rejectionReason.trim();
    if (willSendEmail && !reason) {
      setError('Enter a reason for the rejection notice — it is required before sending email.');
      reasonRef.current?.focus();
      reasonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (willSendEmail && !project.dealId) {
      setError('This RFQ is missing a project link, so notices cannot be sent.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setResultSummary(null);

    const emailRecipients = willSendEmail ? groupQuoteRejectionTargetsForEmail(sendableTargets) : [];

    try {
      let sentCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      let totalSent = 0;
      const emailedOrgIds = new Set<string>();

      if (emailRecipients.length) {
        setProgressLabel(
          `Sending to ${emailRecipients.length} supplier${emailRecipients.length === 1 ? '' : 's'}…`,
        );
        const result = await sendSupplierQuoteRejectionEmails({
          dealId: project.dealId,
          dealTitle,
          projectNumber,
          projectName,
          workType,
          rejectionReason: reason,
          customEmailMessage: customMessage.trim() || undefined,
          senderName,
          rfqProjectId: project.id,
          recipients: emailRecipients,
        });
        const rows = result?.results ?? [];
        if (!rows.length) {
          throw new Error('Vendor email is not configured, so notices were not sent.');
        }
        sentCount = rows.filter(r => r.ok).length;
        skippedCount = rows.filter(r => r.skipped).length;
        failedCount = rows.filter(r => !r.ok && !r.skipped).length;
        totalSent = result.totalSent;
        rows.filter(r => r.ok).forEach(r => emailedOrgIds.add(r.supplierOrgId));
      }

      setProgressLabel('Marking quotes rejected…');
      await persistRejected(selectedTargets, emailedOrgIds);

      const parts: string[] = [
        `Marked ${selectedTargets.length} quote${selectedTargets.length === 1 ? '' : 's'} rejected`,
      ];
      if (sentCount) {
        parts.push(
          `sent ${totalSent} email${totalSent === 1 ? '' : 's'} to ${sentCount} supplier${sentCount === 1 ? '' : 's'}`,
        );
      } else if (!willSendEmail) {
        parts.push('no email sent');
      }
      if (skippedCount) parts.push(`${skippedCount} skipped (missing contact info)`);
      if (failedCount) parts.push(`${failedCount} failed`);
      setResultSummary(`${parts.join('; ')}.`);

      if (failedCount === 0) {
        onClose();
      }
    } catch (err) {
      console.error(err);
      setError(callableErrorMessage(err, willSendEmail ? 'Could not send rejection notices.' : 'Could not reject quotes.'));
    } finally {
      setSubmitting(false);
      setProgressLabel('');
    }
  };

  if (!open || !targets.length) return null;

  const workLabel = workType === 'cnc_quote' ? 'CNC' : workType === 'other_quote' ? 'other quote' : 'timber';
  const allSelected = selectedKeys.size === targets.length;
  const confirmLabel = willSendEmail
    ? `Send ${sendableTargets.length} notice${sendableTargets.length === 1 ? '' : 's'}`
    : `Reject ${selectedTargets.length} quote${selectedTargets.length === 1 ? '' : 's'}`;

  return createPortal(
    <div className="ops-modal-overlay sqam-overlay" role="presentation" onClick={handleClose}>
      <div
        className="ops-modal sqam-modal sqrm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sqrm-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="ops-modal-header sqam-header">
          <div className="sqam-title-block">
            <MailX size={18} aria-hidden className="sqam-title-icon" />
            <h3 id="sqrm-title">Reject {workLabel} quotes</h3>
          </div>
          <button
            type="button"
            className="ops-modal-close"
            aria-label="Close"
            onClick={handleClose}
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body sqam-body">
          <p className="sqam-lead">
            Mark unselected quotes rejected so they drop off Billing &amp; Payables — including quotes on
            earlier RFQ versions. You can notify suppliers by email, or reject in the CRM only.
          </p>

          <section className="sqam-panel">
            <div className="sqrm-recipient-head">
              <h4 className="sqam-section-label">Quotes to reject</h4>
              <button
                type="button"
                className="sqrm-link-btn"
                disabled={submitting}
                onClick={() =>
                  setSelectedKeys(allSelected ? new Set() : new Set(targets.map(quoteRejectionTargetKey)))
                }
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <ul className="sqrm-target-list">
              {targets.map(target => {
                const key = quoteRejectionTargetKey(target);
                const row = recipientRows.find(r => r.orgId === target.orgId)
                  ?? resolvePublishRecipientsForOrgs([target.orgId], supplierOrgById, supplierInvites)[0];
                const canEmail = !!row?.emails.length && !!row.notificationFirstName;
                return (
                  <li key={key} className={`sqrm-target-row${!canEmail ? ' sqrm-target-row--warn' : ''}`}>
                    <label className="sqrm-target-label">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(key)}
                        disabled={submitting}
                        onChange={() => toggleTarget(key)}
                      />
                      <span className="sqrm-target-main">
                        <strong>
                          {target.orgName}
                          {target.versionLabel ? ` · ${target.versionLabel}` : ''}
                        </strong>
                        <span className="sqrm-target-quote">{target.quoteLabel}</span>
                      </span>
                    </label>
                    {sendEmail && !canEmail && (
                      <span className="sqrm-target-warn">No email — mark rejected only</span>
                    )}
                  </li>
                );
              })}
            </ul>
            {missingFirstName.length > 0 && (
              <p className="sqam-warn" role="status">
                <AlertCircle size={14} aria-hidden />
                Add RFQ notification first names in Vendor Management for:{' '}
                {missingFirstName.map(o => o.orgName).join(', ')}. Those quotes can still be marked rejected.
              </p>
            )}
          </section>

          <section className="sqam-panel">
            <label className="sqam-send-toggle">
              <input
                type="checkbox"
                checked={sendEmail}
                disabled={submitting || sendableTargets.length === 0}
                onChange={e => setSendEmail(e.target.checked)}
              />
              <span>Send rejection email to suppliers</span>
            </label>
            {sendableTargets.length === 0 && (
              <p className="sqam-hint">No supplier emails on file for the selected quotes. They will be marked rejected only.</p>
            )}

            <label className="sqam-field-label" htmlFor="sqrm-reason">
              Reason for rejection
            </label>
            <p className="sqam-hint">
              {willSendEmail
                ? 'Required when sending email. Shown in the notice and saved on the quote.'
                : 'Optional. Saved on the quote in the CRM.'}
            </p>
            <textarea
              id="sqrm-reason"
              ref={reasonRef}
              className={`ops-modal-input sqam-textarea sqrm-reason${error && willSendEmail && !rejectionReason.trim() ? ' sqrm-reason--error' : ''}`}
              rows={3}
              value={rejectionReason}
              disabled={submitting}
              placeholder="e.g. We selected another supplier based on lead time and total cost for this release."
              onChange={e => setRejectionReason(e.target.value)}
            />

            {sendEmail && sendableTargets.length > 0 && (
              <>
                <div className="sqrm-preview-picker">
                  <label className="sqam-field-label" htmlFor="sqrm-preview-org">
                    Preview email for
                  </label>
                  <select
                    id="sqrm-preview-org"
                    className="ops-modal-input sqrm-preview-select"
                    value={previewTarget ? quoteRejectionTargetKey(previewTarget) : ''}
                    disabled={submitting}
                    onChange={e => {
                      setPreviewKey(e.target.value);
                      setMessageDirty(false);
                    }}
                  >
                    {targets.map(t => (
                      <option key={quoteRejectionTargetKey(t)} value={quoteRejectionTargetKey(t)}>
                        {t.orgName}
                        {t.versionLabel ? ` · ${t.versionLabel}` : ''} — {t.quoteLabel}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sqam-subject">
                  <span className="sqam-field-label">Subject</span>
                  <span>{emailSubject}</span>
                </div>

                <div className="sqam-compose">
                  <label className="sqam-field-label" htmlFor="sqrm-message">
                    Message
                  </label>
                  <p className="sqam-hint">
                    Adjust the message suppliers receive. The greeting and bid line update per supplier when sent.
                  </p>
                  <textarea
                    id="sqrm-message"
                    className="ops-modal-input sqam-textarea"
                    rows={6}
                    value={customMessage}
                    disabled={submitting}
                    onChange={e => {
                      setMessageDirty(true);
                      setCustomMessage(e.target.value);
                    }}
                  />
                </div>

                <div className="sqam-preview-wrap">
                  <div className="sqam-preview-head">
                    <Mail size={14} aria-hidden />
                    <span>Email preview</span>
                  </div>
                  <div
                    className="sqam-preview-frame sqam-preview-html"
                    title={`Rejection email preview for ${previewTarget?.orgName ?? 'supplier'}`}
                    dangerouslySetInnerHTML={{ __html: emailHtmlForInPagePreview(emailHtml) }}
                  />
                </div>
              </>
            )}
          </section>
        </div>

        <footer className="ops-modal-footer sqam-footer">
          {(progressLabel || resultSummary || error) && (
            <div className="sqrm-footer-status">
              {progressLabel && (
                <p className="sqrm-progress" role="status">
                  <Loader2 size={14} className="sqam-spin" aria-hidden /> {progressLabel}
                </p>
              )}
              {resultSummary && (
                <p className="sqrm-success" role="status">
                  {resultSummary}
                </p>
              )}
              {error && (
                <p className="sqam-error" role="alert">
                  <AlertCircle size={14} aria-hidden /> {error}
                </p>
              )}
            </div>
          )}
          <div className="sqrm-footer-actions">
            <button type="button" className="ops-hide-completed-btn" onClick={handleClose} disabled={submitting}>
              {resultSummary ? 'Close' : 'Skip'}
            </button>
            <button
              type="button"
              className="ops-new-btn"
              disabled={submitting || !selectedTargets.length}
              onClick={() => void handleSend()}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="sqam-spin" aria-hidden /> Saving…
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default SupplierQuoteRejectionModal;
